import {playlistAudioReferences,touchFeedbackSettings} from "./playlist-audio.js";
import {effectiveStaticOverlays} from "./static-overlay-collection.js";
import { validateOverlayPlaylist } from "./hotspot-overlay.js";
const encoder = new TextEncoder();
const decoder = new TextDecoder("utf-8", { fatal: true });
export function crc32(bytes) {
  let crc = 0xffffffff;
  for (const value of bytes) { crc ^= value;for (let bit = 0; bit < 8; bit++) crc = (crc >>> 1) ^ ((crc & 1) ? 0xedb88320 : 0); }
  return (~crc) >>> 0;
}
export function encodeUsbFrame(type, requestId, payload, flags = 0) {
  if (!(payload instanceof Uint8Array) || payload.length > 8192) throw new Error("Invalid USB payload size.");
  const bytes = new Uint8Array(20 + payload.length), view = new DataView(bytes.buffer);
  bytes.set([80, 82, 68, 89, 1, type]);view.setUint16(6, flags, true);
  view.setUint32(8, requestId, true);view.setUint32(12, payload.length, true);bytes.set(payload, 16);
  view.setUint32(16 + payload.length, crc32(bytes.subarray(4, 16 + payload.length)), true);return bytes;
}
export class UsbFrameReader {
  buffer = new Uint8Array();
  push(chunk) {
    const combined = new Uint8Array(this.buffer.length + chunk.length);combined.set(this.buffer);combined.set(chunk, this.buffer.length);
    this.buffer = combined;const frames = [];
    while (this.buffer.length >= 4) {
      const b = this.buffer;
      if (b[0] !== 80 || b[1] !== 82 || b[2] !== 68 || b[3] !== 89) { this.buffer = b.slice(1);continue; }
      if (b.length < 16) break;
      const view = new DataView(b.buffer, b.byteOffset, b.byteLength), length = view.getUint32(12, true);
      if (b[4] !== 1 || length > 8192) throw new Error("Unsupported or oversized Pirdy USB response.");
      if (b.length < length + 20) break;
      if (view.getUint32(16 + length, true) !== crc32(b.subarray(4, 16 + length))) throw new Error("Pirdy USB response verification failed.");
      frames.push({ type: b[5], flags: view.getUint16(6, true), id: view.getUint32(8, true), payload: b.slice(16, 16 + length) });
      this.buffer = b.slice(length + 20);
    }
    return frames;
  }
}

export class PirdyBrowserUsb {
  constructor({ serial = globalThis.navigator?.serial, timeoutMs = 8000 } = {}) { this.serial = serial;this.timeoutMs = timeoutMs;this.nextId = 1; }
  async connect() {
    if (this.port) throw new Error("Disconnect the current Pirdy first.");
    if (!this.serial) throw new Error("USB Local Control requires desktop Chrome or Edge. Use the Windows app in other browsers.");
    const port = await this.serial.requestPort({ filters: [{ usbVendorId: 0x1a86 }, { usbVendorId: 0x10c4 }, { usbVendorId: 0x303a }, { usbVendorId: 0x0403 }] });
    try {
      await port.open({ baudRate: 115200, dataBits: 8, stopBits: 1, parity: "none", flowControl: "none", bufferSize: 65536 });
      this.port = port;this.portOpen = true;this.transferBaudRate = 115200;this.speedAttempted = false;this.faulted = false;await port.setSignals({ dataTerminalReady: false, requestToSend: false });
      this.reader = port.readable.getReader();this.writer = port.writable.getWriter();this.reading = this.readLoop();
      const hello = await this.request(1, { client: "Pirdy Web App", purpose: "control", minimumProtocol: 1, maximumProtocol: 1 });
      if (hello.protocolVersion !== 1 || !/^[a-f0-9]{32}$/i.test(hello.session ?? "") || !hello.hardwareUid || !hello.hardwareProfile || !Array.isArray(hello.capabilities) || !hello.capabilities.includes("USB_STORAGE_V1")) throw new Error("The selected port did not identify itself as a compatible Pirdy.");
      this.session = hello.session;this.device = hello;
      this.keepalive = setInterval(() => { if (!this.pending && !this.queued) this.request(18).catch(() => this.disconnect()); }, 10000);
      return hello;
    } catch (error) { await this.disconnect();throw error; }
  }
  async readLoop() {
    const parser = new UsbFrameReader();
    try {
      while (true) {
        const { value, done } = await this.reader.read();if (done) break;
        for (const frame of parser.push(value)) {
          const pending = this.pending;
          if (!pending || frame.id !== pending.id) continue;
          if (!(frame.flags & 1) || (frame.flags & ~3) || frame.type !== pending.type) throw new Error("Unexpected USB acknowledgement.");
          if (frame.flags & 2) {
            const error = new Error("The device rejected this action. Refresh its status before trying again.");
            try { error.deviceCode = JSON.parse(decoder.decode(frame.payload)).code; } catch { }
            pending.reject(error);
          } else pending.resolve(frame.payload);
        }
      }
    } catch { this.pending?.reject(new Error("USB communication failed. Reconnect your Pirdy; close other apps using its USB port.")); }
    finally { this.pending?.reject(new Error("Pirdy was disconnected."));this.reader?.releaseLock();this.reader = null; }
  }
  request(type, value = {}, timeoutMs = this.timeoutMs) {
    this.queued = (this.queued || 0) + 1;
    const result = (this.queue || Promise.resolve()).then(() => this.exchange(type, value, timeoutMs));
    this.queue = result.catch(() => {}).finally(() => { this.queued--; });
    return result;
  }
  async exchange(type, value = {}, timeoutMs = this.timeoutMs) {
    const payload = value instanceof Uint8Array ? value : encoder.encode(JSON.stringify({ ...value, ...(this.session ? { session: this.session } : {}) }));
    if (!this.writer || this.faulted) throw new Error("Reconnect your Pirdy before issuing another USB action.");
    if (type !== 1 && !this.session) throw new Error("Pirdy identity verification is required.");
    if (this.pending) throw new Error("Wait for the current USB action to finish.");
    const id = this.nextId++;let timer;
    const response = new Promise((resolve, reject) => {
      this.pending = { id, type, resolve, reject };
      timer = setTimeout(() => reject(new Error("Pirdy did not confirm the action. Reconnect and check its state before retrying.")), timeoutMs);
    });
    // Install the rejection observer before the asynchronous write.
    const observed = response.then(bytes => type === 7 ? bytes : JSON.parse(decoder.decode(bytes)));
    observed.catch(() => {});
    try { const [, result] = await Promise.all([this.writer.write(encodeUsbFrame(type, id, payload)), observed]);return result; }
    catch (error) {
      // A verified missing-file response is the normal incremental-upload path.
      if (!(type === 5 && error.deviceCode === "FileNotFound")) { this.faulted = true;this.session = null; }
      throw error;
    }
    finally { clearTimeout(timer);this.pending = null; }
  }
  require(capability) { if (!this.device?.capabilities.includes(capability)) throw new Error("This firmware does not support the selected USB control."); }
  async reopenTransport(baudRate) {
    await this.reader?.cancel();await this.reading;
    this.writer?.releaseLock();this.writer = null;
    if (this.portOpen) { await this.port.close();this.portOpen = false; }
    await this.port.open({ baudRate, dataBits: 8, stopBits: 1, parity: "none", flowControl: "none", bufferSize: 65536 });
    this.portOpen = true;this.transferBaudRate = baudRate;
    await this.port.setSignals({ dataTerminalReady: false, requestToSend: false });
    this.reader = this.port.readable.getReader();this.writer = this.port.writable.getWriter();this.reading = this.readLoop();
  }
  async enableFastTransfer() {
    if (this.speedAttempted || this.transferBaudRate === 921600 || !this.device?.capabilities.includes("SERIAL_FAST_921600_V1")) return;
    this.speedAttempted = true;
    try {
      const reply = await this.request(30, { baudRate: 921600 });
      if (reply.baudRate !== 921600) throw new Error("Invalid transfer speed acknowledgement.");
      await this.reopenTransport(921600);await this.request(18);
    } catch {
      // No file mutation has started. The firmware resets an unconfirmed speed
      // change after two seconds. Reverify the exact device before any write.
      await new Promise(resolve => setTimeout(resolve, 2200));
      await this.reopenTransport(115200);this.faulted = false;this.session = null;
      await this.purpose("storage");
    }
  }
  async assetAlreadyPresent(path, size, hash, isCurrent) {
    if (!path.startsWith("/assets/")) return false;
    try {
      const existing = await this.request(5, { path });
      if (!isCurrent()) throw new Error("The account or USB selection changed.");
      return existing.size === size && existing.sha256?.toLowerCase() === hash.toLowerCase();
    } catch (error) { if (error.deviceCode === "FileNotFound") return false;throw error; }
  }
  async completeContentTransfer(isCurrent = () => true) {
    // Initial-frame preparation can exceed an interactive-control read timeout.
    // Keep completion bounded and read-only; never repeat the committed write.
    await this.purpose("control", 30000);
    if (!this.device?.capabilities.includes("USB_TRANSFER_COMPLETION_V1")) return;
    const deadline = Date.now() + 120000;
    while (true) {
      if (!isCurrent()) throw new Error("The account or USB selection changed; check the display before retrying.");
      const status = await this.request(31, {}, 30000);
      if (typeof status.transferActive !== "boolean" || typeof status.playbackReady !== "boolean") throw new Error("Invalid transfer completion response.");
      if (!status.transferActive && status.playbackReady) return;
      if (Date.now() >= deadline) throw new Error("Files were verified but Pirdy has not confirmed returning to playback. Check its screen before retrying.");
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }
  async display(displayOn) {
    this.require("DISPLAY_POWER_V1");const result = await this.request(25, { displayOn });
    if (result.displayOn !== displayOn || !result.persisted || !result.wifiUnchanged) throw new Error("The device did not confirm display power.");
    this.device.displayOn = displayOn;return result;
  }
  async experience() { this.require("CYD_EXPERIENCE_V1");return this.request(28); }
  async purpose(purpose, timeoutMs = this.timeoutMs) {
    const expected = this.device;
    const hello = await this.request(1, { client: "Pirdy Web App", purpose, minimumProtocol: 1, maximumProtocol: 1 }, timeoutMs);
    if (hello.protocolVersion !== 1 || !/^[a-f0-9]{32}$/i.test(hello.session ?? "") || hello.hardwareUid !== expected?.hardwareUid || hello.deviceId !== expected?.deviceId || hello.hardwareProfile !== expected?.hardwareProfile) {
      this.session = null;this.faulted = true;throw new Error("The USB identity changed. Reconnect your Pirdy.");
    }
    this.session = hello.session;
  }
  async applyExperience(values) {
    this.require("CYD_EXPERIENCE_V1");let result = await this.request(29, values);
    for (let i = 0; result.pending && i < 50; i++) { await new Promise(resolve => setTimeout(resolve, 100));result = await this.experience(); }
    if (result.pending || result.error) throw new Error("The device has not confirmed this change. Refresh before retrying.");
    return result;
  }
  async claimPin() {
    this.require("CLAIM_PIN_OVER_USB_V1");await this.purpose("storage");
    try { const result = await this.request(24);
      if (result.deviceId !== this.device.deviceId) throw new Error("The USB identity changed. Reconnect your Pirdy.");
      return result;
    } finally { if (!this.faulted) await this.purpose("control"); }
  }
  validateManifest(bytes) {
      const manifest=JSON.parse(new TextDecoder().decode(bytes));
      if(playlistAudioReferences(manifest.items).length)this.require("PLAYLIST_AUDIO_V1");
      if(manifest.items?.some(item=>item.backgroundAudio!=null)||playlistAudioReferences(manifest.items).length>16)this.require("ARTWORK_BACKGROUND_AUDIO_V1");
      if(manifest.items?.some(item=>item.interactions?.touchAreas?.some(area=>Number(area.action?.kind)===7)))this.require("HOTSPOT_KEEP_ARTWORK_V1");
      if(manifest.items?.some(item=>Number(touchFeedbackSettings(item.touchFeedback)?.sound)>0))this.require("TOUCH_FEEDBACK_V1");
      if(validateOverlayPlaylist(manifest.items)>0)this.require("HOTSPOT_OVERLAY_V1");
      if(manifest.items?.some(item=>effectiveStaticOverlays(item).length))this.require("STATIC_OVERLAY_V1");
      if(manifest.items?.some(item=>item.staticOverlays!=null&&effectiveStaticOverlays(item).length))this.require("MULTI_STATIC_OVERLAY_V1");
      if(manifest.items?.some(item=>item.axis==="vertical"&&Number(item.speedPxPerSecond)>25))this.require("VERTICAL_SCROLL_60");
      if(bytes.length>24576)throw new Error("This playlist exceeds the device metadata limit.");
  }
  async upload(path, bytes, isCurrent = () => true, progress = () => {}) {
    this.require("WRITE");
    if (!(bytes instanceof Uint8Array) || !bytes.length || bytes.length > (path.endsWith(".wav") ? 230500000 : 32 * 1024 * 1024) ||
        !(path === "/manifests/active.json" || path==="/branding/startup.wav"&&bytes.length<=327680 || path==="/branding/active.pimg"&&bytes.length<=1048576 || /^\/assets\/[a-f0-9]{64}\.(pimg|wav)$/.test(path))) throw new Error("Invalid local content file.");
    if(path==="/manifests/active.json")this.validateManifest(bytes);
    const hash = Array.from(new Uint8Array(await crypto.subtle.digest("SHA-256", bytes)), x => x.toString(16).padStart(2, "0")).join("");
    if (path.startsWith("/assets/") && !path.includes(hash)) throw new Error("Content hash does not match its filename.");
    if (!isCurrent()) throw new Error("The account or USB selection changed.");
    if (await this.assetAlreadyPresent(path, bytes.length, hash, isCurrent)) { progress(bytes.length, bytes.length);return; }
    await this.enableFastTransfer();
    if (!isCurrent()) throw new Error("The account or USB selection changed.");
    const begin = await this.request(9, { path, size: bytes.length, sha256: hash });
    if (!Number.isInteger(begin.transferId) || begin.transferId <= 0 || begin.transferId > 0xffffffff || begin.verifiedOffset !== 0 || !Number.isInteger(begin.chunkBytes) || begin.chunkBytes < 256 || begin.chunkBytes > 2048) throw new Error("The device returned invalid transfer metadata.");
    for (let offset = 0; offset < bytes.length;) {
      if (!isCurrent()) throw new Error("Transfer cancelled before activation.");
      const count = Math.min(begin.chunkBytes, bytes.length - offset), payload = new Uint8Array(44 + count), view = new DataView(payload.buffer);
      payload.set(encoder.encode(this.session));view.setUint32(32, begin.transferId, true);view.setBigUint64(36, BigInt(offset), true);payload.set(bytes.subarray(offset, offset + count), 44);
      const acknowledgement = await this.request(10, payload);
      if (acknowledgement.offset !== offset + count) throw new Error("The device acknowledged an unexpected transfer position.");
      offset += count;progress(offset, bytes.length);
    }
    if (!isCurrent()) throw new Error("Transfer cancelled before activation.");
    const completed = await this.request(11, { transferId: begin.transferId });
    if (completed.size !== bytes.length || completed.sha256?.toLowerCase() !== hash) throw new Error("The device did not verify the uploaded content.");
    if (path === "/manifests/active.json") {
      for (let i = 0; i < 100; i++) {
        if (!isCurrent()) throw new Error("Account changed; check the device to confirm the activation outcome.");
        const active = await this.request(5, { path });
        if (active.size === bytes.length && active.sha256?.toLowerCase() === hash) { await this.completeContentTransfer(isCurrent);return; }
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      throw new Error("Content was uploaded but activation is not confirmed. Check the device before retrying.");
    }
  }
  async download(path, isCurrent = () => true) {
    if (!["/events/cyd.csv", "/manifests/active.json"].includes(path)) throw new Error("This local download is unavailable.");
    this.require("READ");await this.purpose("storage");let lease, opened=false;
    try {
      if (!isCurrent()) throw new Error("The account changed.");
      lease=await this.request(6,{path});opened=true;
      const limit=path.endsWith(".csv")?131072:24576;
      if (!Number.isInteger(lease.transferId) || lease.transferId<=0 || !Number.isInteger(lease.size) || lease.size<0 || lease.size>limit || !/^[a-f0-9]{64}$/i.test(lease.sha256 ?? "")) throw new Error("The device returned invalid download metadata.");
      const bytes=new Uint8Array(lease.size);
      for(let offset=0;offset<bytes.length;) {
        if(!isCurrent()) throw new Error("Download cancelled after the account changed.");
        const length=Math.min(2048,bytes.length-offset), block=await this.request(7,{transferId:lease.transferId,offset,length});
        if(!(block instanceof Uint8Array)||block.length<=17||block.length>17+length) throw new Error("Invalid device download block.");
        const view=new DataView(block.buffer,block.byteOffset,block.byteLength),count=block.length-17;
        if(view.getBigUint64(0,true)!==BigInt(offset)||view.getBigUint64(8,true)!==BigInt(bytes.length)||block[16]!==Number(offset+count===bytes.length)) throw new Error("Device download is incomplete or out of order.");
        bytes.set(block.subarray(17),offset);offset+=count;
      }
      await this.request(8,{transferId:lease.transferId});opened=false;
      const hash=Array.from(new Uint8Array(await crypto.subtle.digest("SHA-256",bytes)),x=>x.toString(16).padStart(2,"0")).join("");
      if(!isCurrent()||hash!==lease.sha256.toLowerCase()) throw new Error("The download could not be verified.");
      return bytes;
    } finally {
      if(!this.faulted&&this.session) {
        if(opened) await this.request(17,{closeSession:false});
        await this.purpose("control");
      }
    }
  }
  async disconnect() {
    clearInterval(this.keepalive);this.keepalive = null;
    if (this.session && this.writer && !this.pending) {
      try { await this.request(17, { closeSession: true }); } catch { }
    }
    this.session = null;this.device = null;
    this.pending?.reject(new Error("USB connection closed."));
    try { await this.reader?.cancel();await this.reading; } catch { }
    try { await this.writer?.abort();this.writer?.releaseLock(); } catch { }this.writer = null;
    try { if (this.portOpen) await this.port?.close(); } catch { }this.port = null;this.portOpen = false;
  }
}
